import type { SupabaseClient } from '@supabase/supabase-js';
import { loadMappingSources, type FlatSource } from './mapping-helpers.js';
import { registerOverviewSave } from '../../lib/overview-save-registry.js';

// ============================================================================
// Per-Artikel Stammdaten-Mapping (Tab „Übersicht & Mapping"). Sparse-Override über
// dem globalen Einstellungs-Mapping: pro Feld 4 Modi.
//   inherit (Vorlage) = Startzustand, folgt der globalen Regel - wird NICHT gespeichert.
//   vario_path / fixed_value / unmapped = Artikel-Override (gespeichert in mapping_override).
// Persistenz: gambio_get_article_mapping / gambio_save_article_mapping.
// WICHTIG: mapping_override wird mit dem Zusatzfelder-Tab GETEILT → beim Speichern die
// 'additional'-Zeilen des bestehenden Overrides erhalten (nur 'product_data_basic' ersetzen).
// ============================================================================

export interface PerArticleMappingCtx { supabase: SupabaseClient; supabaseUrl: string; session: { token: string }; }
export interface PerArticleMappingArgs { listingId: string; varioArticleId: string; }

interface SavedField {
  gambio_field_key: string; field_group: string; source_type: string;
  vario_path: string | null; fixed_value: unknown; template: string | null;
}
interface FieldRow { key: string; label: string; hint: string; }

const FIELDS: FieldRow[] = [
  { key: 'name',         label: 'Titel',         hint: 'Produktname in Gambio' },
  { key: 'description',  label: 'Beschreibung',  hint: 'Langtext' },
  { key: 'priceNet',     label: 'Preis (netto)', hint: 'Basispreis netto' },
  { key: 'ean',          label: 'EAN',           hint: 'GTIN/EAN' },
  { key: 'sku',          label: 'Modell/SKU',    hint: 'Artikelnummer' },
  { key: 'manufacturer', label: 'Hersteller',    hint: 'Herstellername' },
];

const MODES: Array<{ value: string; label: string }> = [
  { value: 'inherit',     label: 'Vorlage (global)' },
  { value: 'vario_path',  label: 'Vario-Feld' },
  { value: 'fixed_value', label: 'Fester Wert' },
  { value: 'unmapped',    label: 'Nicht gemappt' },
];

function esc(s: string | null | undefined): string {
  if (s == null) return '';
  return String(s).replaceAll('&','&amp;').replaceAll('<','&lt;').replaceAll('>','&gt;').replaceAll('"','&quot;').replaceAll("'",'&#39;');
}

/** Lesbare „erbt:"-Zeile für den Vorlage-Modus: Klartext-Feldname statt rohem Vario-Pfad
 *  plus - soweit die Quelle einen Beispielwert für diesen Artikel liefert - den aktuellen Wert.
 *  `srcByPath` bildet den gespeicherten bare-Pfad auf die angereicherte Quelle (Label + example) ab. */
function globalHint(g: SavedField | undefined, srcByPath: Map<string, FlatSource>): string {
  if (!g || g.source_type === 'unmapped') return 'erbt: nicht gemappt';
  if (g.source_type === 'vario_path') {
    const path = g.vario_path ?? '';
    const src = path ? srcByPath.get(path) : undefined;
    const name = src?.label ?? (path || '—');
    const value = src?.example ? ` - ${src.example}` : '';
    const pathTail = path ? ` (${path})` : '';
    return `erbt: ${name}${value}${pathTail}`;
  }
  if (g.source_type === 'fixed_value') return `erbt: fester Wert „${typeof g.fixed_value === 'string' ? g.fixed_value : ''}"`;
  if (g.source_type === 'template')    return `erbt: Formel ${g.template ?? ''}`;
  return 'erbt: —';
}

export async function renderPerArticleMapping(host: HTMLElement, ctx: PerArticleMappingCtx, args: PerArticleMappingArgs): Promise<void> {
  host.innerHTML = `<div class="text-grey">Lade Mapping …</div>`;
  const { data, error } = await ctx.supabase.rpc('gambio_get_article_mapping', { _listing_id: args.listingId });
  if (error) { host.innerHTML = `<div class="v-banner v-banner-danger"><div>Mapping nicht ladbar: ${esc(error.message)}</div></div>`; return; }
  const payload = (data as { override?: SavedField[]; global?: SavedField[] } | null) ?? {};
  const override = new Map<string, SavedField>((payload.override ?? []).map((f) => [f.gambio_field_key, f]));
  const globalMap = new Map<string, SavedField>((payload.global ?? []).map((f) => [f.gambio_field_key, f]));
  const preservedAdditional = (payload.override ?? []).filter((f) => f.field_group === 'additional');

  // Vario-Quellen dynamisch (gambio-mapping-sources) inkl. Beispiel-/EAV-Feldern dieses Artikels.
  const sources: FlatSource[] = await loadMappingSources(
    { supabaseUrl: ctx.supabaseUrl, session: ctx.session },
    { articleId: args.varioArticleId },
  );
  const srcByPath = new Map<string, FlatSource>(sources.map((s) => [s.path, s]));
  const optsHtml = (vp: string): string =>
    sources.map((s) => {
      const suffix = s.example ? ` - ${s.example}` : '';
      return `<option value="${esc(s.path)}"${s.path === vp ? ' selected' : ''}>${esc(s.label + suffix)}</option>`;
    }).join('');

  // Spaltenreihenfolge: Modus ZUERST, dann Vario-Quelle, dann Gambio-Zielfeld - der Modus schaltet
  // die Vario-Spalte um und steht deshalb links davor (Betreiber-Ansage 21.08.2026, identisch zum
  // globalen Artikel-Mapping). Grid und Kopfzeile teilen dieselbe Definition.
  const AM_GRID = '160px 1fr 170px';
  const headHtml = (): string => `
    <div style="display:grid;grid-template-columns:${AM_GRID};gap:8px;padding:0 0 6px;border-bottom:1px solid var(--go-border,#e5e7eb);font-size:11px;font-weight:700;letter-spacing:.04em;text-transform:uppercase;color:var(--go-muted,#6b7280);">
      <div>Modus</div><div>Vario</div><div>Gambio</div>
    </div>`;

  const rowHtml = (f: FieldRow): string => {
    const o = override.get(f.key);
    const st = o?.source_type ?? 'inherit';
    const vp = o?.vario_path ?? '';
    const fv = typeof o?.fixed_value === 'string' ? o.fixed_value : '';
    const opts = optsHtml(vp);
    return `
      <div class="am-row" data-field="${esc(f.key)}" style="display:grid;grid-template-columns:${AM_GRID};gap:8px;align-items:start;padding:6px 0;border-bottom:1px solid var(--go-border,#e5e7eb);">
        <div class="v-select"><select data-am-mode="${esc(f.key)}">
          ${MODES.map((m) => `<option value="${m.value}"${m.value === st ? ' selected' : ''}>${m.label}</option>`).join('')}
        </select></div>
        <div>
          <div data-am-ctl="inherit" data-for="${esc(f.key)}" hidden>
            <span class="v-text-muted" style="font-size:12px;">${esc(globalHint(globalMap.get(f.key), srcByPath))}</span></div>
          <div class="v-select" data-am-ctl="vario_path" data-for="${esc(f.key)}" hidden><select data-am-vp>
            <option value="">— Vario-Feld wählen —</option>${opts}
          </select></div>
          <div class="v-input" data-am-ctl="fixed_value" data-for="${esc(f.key)}" hidden>
            <input type="text" data-am-fv placeholder="Fester Wert" value="${esc(fv)}" /></div>
          <div data-am-ctl="unmapped" data-for="${esc(f.key)}" hidden>
            <span class="v-text-muted" style="font-size:12px;">Wird für diesen Artikel nicht übertragen.</span></div>
        </div>
        <div><strong>${esc(f.label)}</strong><div class="v-text-muted" style="font-size:11px;">${esc(f.hint)}</div></div>
      </div>`;
  };

  host.innerHTML = `
    <h4 class="klst-detail-section" style="margin:0 0 6px;">Feld-Mapping (Artikel)</h4>
    <p class="v-text-muted" style="font-size:12px;margin:0 0 10px;">„Vorlage" folgt dem globalen Mapping. Nur bewusste Abweichungen werden am Artikel gespeichert.</p>
    <div class="am-fields">${headHtml()}${FIELDS.map(rowHtml).join('')}</div>`;

  const sync = (key: string, mode: string): void => {
    host.querySelectorAll<HTMLElement>(`[data-for="${CSS.escape(key)}"]`).forEach((el) => {
      const show = el.getAttribute('data-am-ctl') === mode;
      // Inline-display setzen: `.v-select { display:inline-flex }` schlaegt sonst das
      // `[hidden]`-Attribut (UA `display:none`) → falsche Steuer-Elemente blieben sichtbar.
      el.hidden = !show;
      el.style.display = show ? '' : 'none';
    });
  };
  host.querySelectorAll<HTMLSelectElement>('[data-am-mode]').forEach((sel) => {
    const key = sel.getAttribute('data-am-mode')!;
    sel.addEventListener('change', () => sync(key, sel.value));
    sync(key, sel.value);
  });

  // Kein eigener Speichern-Button mehr - der eine „Speichern" im Übersicht-Tab-Kopf ruft diese
  // Funktion über das overview-save-registry auf (Chef-Wunsch: EIN Button für den ganzen Tab).
  async function runSave(): Promise<{ ok: boolean; msg?: string }> {
    // mapping_override wird mit dem Zusatzfelder-Tab GETEILT → dessen 'additional'-Zeilen IMMER
    // frisch nachladen (read-merge-write), nicht den Mount-Snapshot nutzen - sonst überschreibt ein
    // späterer Save hier zwischenzeitlich gespeicherte Zusatzfeld-Overrides.
    let curAdditional: SavedField[] = preservedAdditional;
    try {
      const { data: curData } = await ctx.supabase.rpc('gambio_get_article_mapping', { _listing_id: args.listingId });
      curAdditional = (((curData as { override?: SavedField[] } | null)?.override) ?? []).filter((f) => f.field_group === 'additional');
    } catch { /* Fallback: Mount-Snapshot */ }
    const fields: SavedField[] = [...curAdditional];
    host.querySelectorAll<HTMLElement>('.am-row').forEach((row) => {
      const key = row.getAttribute('data-field')!;
      const mode = row.querySelector<HTMLSelectElement>('[data-am-mode]')!.value;
      if (mode === 'inherit') return;
      const entry: SavedField = { gambio_field_key: key, field_group: 'product_data_basic', source_type: mode, vario_path: null, fixed_value: null, template: null };
      if (mode === 'vario_path')  entry.vario_path  = row.querySelector<HTMLSelectElement>('[data-am-vp]')?.value || null;
      if (mode === 'fixed_value') entry.fixed_value = row.querySelector<HTMLInputElement>('[data-am-fv]')?.value ?? '';
      fields.push(entry);
    });
    const { error: saveErr } = await ctx.supabase.rpc('gambio_save_article_mapping', { _listing_id: args.listingId, _fields: fields });
    if (saveErr) return { ok: false, msg: `Artikel-Mapping: ${saveErr.message}` };
    return { ok: true };
  }
  registerOverviewSave(host, { label: 'Artikel-Mapping', run: runSave });
}
